/**
 * [화면 편집] 오른쪽 칸의 입력들 — 쉬운 판(2026-10-30). 숫자를 치기보다 누르고 끄는 것으로 고친다: 색은 동그란 견본,
 * 글자 크기는 [−]·[+], 둥글기·투명도는 밀대. 이름은 모두 우리말로 쓴다.
 *
 * 밀대·색 고르기처럼 잇달아 바뀌는 것은 `final` 을 false 로 넘긴다 — 편집기가 잠깐 뒤에 한 번만 되돌리기 칸으로 남긴다.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Check, Minus, Plus } from 'lucide-react';
import { DateField } from '../components/DateField.js';
import { rgbHex, type Rgba } from './model.js';

/** 기본 견본 — 어느 화면에나 무난한 열 가지. 이 화면이 쓰는 색은 그 앞에 따로 둔다. */
export const PALETTE: ReadonlyArray<{ hex: string; name: string }> = [
  { hex: '#000000', name: '검정' },
  { hex: '#ffffff', name: '흰색' },
  { hex: '#6b7280', name: '회색' },
  { hex: '#dc2626', name: '빨강' },
  { hex: '#f97316', name: '주황' },
  { hex: '#facc15', name: '노랑' },
  { hex: '#16a34a', name: '초록' },
  { hex: '#2563eb', name: '파랑' },
  { hex: '#1e3a8a', name: '남색' },
  { hex: '#7c3aed', name: '보라' },
];

/** 칸 묶음 — 굵은 제목 한 줄과 그 아래 내용. */
export function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className="border-b border-slate-100 px-4 py-3.5">
      <h4 className="mb-2 text-[12px] font-semibold text-slate-500">{title}</h4>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

/** 글자가 붙은 단추 — 오른쪽 칸의 동작(글 고치기·가운데로 등). */
export function PanelButton({
  icon,
  label,
  onClick,
  tone = 'plain',
  disabled = false,
  title,
}: {
  icon?: ReactNode;
  label: string;
  onClick: () => void;
  tone?: 'plain' | 'primary' | 'danger';
  disabled?: boolean;
  title?: string;
}): ReactNode {
  const tones = {
    plain: 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    danger: 'border border-red-200 bg-white text-red-600 hover:bg-red-50',
  };
  return (
    <button
      type="button"
      title={title ?? label}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium disabled:cursor-default disabled:opacity-40 ${tones[tone]}`}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

/** 몇 개 가운데 하나를 고르는 단추 줄(정렬 등). */
export function Choice<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T | null;
  options: ReadonlyArray<{ value: T; label: string; icon?: ReactNode; title?: string }>;
  onChange: (value: T) => void;
  label: string;
}): ReactNode {
  return (
    <div role="group" aria-label={label} className="flex gap-1 rounded-lg bg-slate-100 p-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          title={option.title ?? option.label}
          aria-label={option.title ?? option.label}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={`flex h-8 flex-1 items-center justify-center gap-1 rounded-md text-[12.5px] font-medium ${
            value === option.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** 켜고 끄는 단추(굵게 등). */
export function Toggle({
  on,
  onChange,
  icon,
  label,
}: {
  on: boolean;
  onChange: (on: boolean) => void;
  icon?: ReactNode;
  label: string;
}): ReactNode {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={() => onChange(!on)}
      className={`flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 text-[13px] font-medium ${
        on ? 'bg-blue-600 text-white' : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

/** 숫자를 [−]·[+] 로 — 가운데 칸에 쳐서 넣어도 된다. `step` 은 지금 값에서 다음 값을 낸다(글자 크기는 10%씩). */
export function Stepper({
  value,
  onChange,
  step,
  min = 1,
  max = 999,
  suffix,
  label,
}: {
  value: number;
  onChange: (value: number) => void;
  step: (value: number, direction: 1 | -1) => number;
  min?: number;
  max?: number;
  suffix?: string;
  label: string;
}): ReactNode {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const clamp = (next: number): number => Math.max(min, Math.min(max, Math.round(next)));
  const commitText = (): void => {
    const parsed = Number.parseFloat(text);
    if (Number.isFinite(parsed) && clamp(parsed) !== value) onChange(clamp(parsed));
    else setText(String(value));
  };
  const button =
    'grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40';
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        aria-label={`${label} 줄이기`}
        className={button}
        disabled={value <= min}
        onClick={() => onChange(clamp(step(value, -1)))}
      >
        <Minus className="h-4 w-4" />
      </button>
      <label className="flex h-9 min-w-0 flex-1 items-center rounded-lg border border-slate-200 bg-white px-2 focus-within:border-blue-500">
        <input
          aria-label={label}
          inputMode="numeric"
          className="h-full min-w-0 flex-1 bg-transparent text-center text-[14px] font-semibold text-slate-900 outline-none"
          value={text}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => setText(event.target.value)}
          onBlur={commitText}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
            if (event.key === 'Escape') {
              setText(String(value));
              event.currentTarget.blur();
            }
          }}
        />
        {suffix ? <span className="shrink-0 text-[12px] text-slate-400">{suffix}</span> : null}
      </label>
      <button
        type="button"
        aria-label={`${label} 늘리기`}
        className={button}
        disabled={value >= max}
        onClick={() => onChange(clamp(step(value, 1)))}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

/** 밀대 — 끄는 동안 화면이 바로 바뀐다. */
export function Slider({
  label,
  value,
  min,
  max,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  suffix: string;
  onChange: (value: number) => void;
}): ReactNode {
  return (
    <label className="block">
      <span className="flex items-center justify-between text-[12.5px] text-slate-700">
        {label}
        <span className="tabular-nums text-slate-500">
          {value}
          {suffix}
        </span>
      </span>
      <input
        type="range"
        className="mt-1 w-full accent-blue-600"
        min={min}
        max={max}
        value={Math.max(min, Math.min(max, value))}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

/** 숫자 칸 하나 — [숫자로 맞추기] 에서. 치고 Enter 나 다른 곳을 누르면 반영된다. */
export function NumberField({
  label,
  value,
  min,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  onChange: (value: number) => void;
}): ReactNode {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);
  const commitText = (): void => {
    const parsed = Number.parseFloat(text);
    if (!Number.isFinite(parsed)) return setText(String(value));
    const next = Math.round(min === undefined ? parsed : Math.max(min, parsed));
    if (next !== value) onChange(next);
    else setText(String(value));
  };
  return (
    <label className="block">
      <span className="text-[12px] text-slate-500">{label}</span>
      <input
        inputMode="numeric"
        className="mt-0.5 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[13px] tabular-nums text-slate-900 outline-none focus:border-blue-500"
        value={text}
        onFocus={(event) => {
          setFocused(true);
          event.currentTarget.select();
        }}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => {
          setFocused(false);
          commitText();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
    </label>
  );
}

/**
 * 색 고르기 — 이 화면이 쓰는 색 · 기본 견본 · [다른 색](색 고르기 창) · [없음]. 지금 색에는 ✓ 가 붙는다.
 * 견본을 누르면 바로 남기고(`final`), 색 고르기 창은 고르는 동안 잇달아 바뀌므로 잠깐 뒤에 남긴다.
 */
export function ColorChoices({
  label,
  current,
  docColors,
  onPick,
  none,
}: {
  label: string;
  /** 지금 색 — 투명하면 null. */
  current: Rgba | null;
  docColors: readonly string[];
  onPick: (css: string | null, final: boolean) => void;
  /** [없음] 견본의 이름(예: "없음", "원래대로") — 주지 않으면 견본이 없다. */
  none?: string;
}): ReactNode {
  const hex = current && current.a > 0 ? rgbHex(current) : null;
  const known = new Set([...docColors, ...PALETTE.map((color) => color.hex)]);
  return (
    <div role="group" aria-label={label}>
      {docColors.length > 0 ? (
        <>
          <div className="mb-1 text-[11.5px] text-slate-400">이 화면의 색</div>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {docColors.map((color) => (
              <Swatch key={color} color={color} name={color} on={hex === color} onClick={() => onPick(color, true)} />
            ))}
          </div>
        </>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {PALETTE.map((color) => (
          <Swatch
            key={color.hex}
            color={color.hex}
            name={color.name}
            on={hex === color.hex}
            onClick={() => onPick(color.hex, true)}
          />
        ))}
        <label
          title="다른 색 고르기"
          className={`relative grid h-7 w-7 cursor-pointer place-items-center rounded-full ring-1 ring-black/10 ${
            hex && !known.has(hex) ? 'ring-2 ring-blue-600 ring-offset-1' : ''
          }`}
          style={{
            background: 'conic-gradient(#ef4444, #f59e0b, #22c55e, #06b6d4, #3b82f6, #a855f7, #ef4444)',
          }}
        >
          <span className="h-3 w-3 rounded-full bg-white" />
          <input
            type="color"
            aria-label={`${label} — 다른 색`}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            value={hex ?? '#000000'}
            onChange={(event) => onPick(event.target.value, false)}
          />
        </label>
        {none ? (
          <button
            type="button"
            title={none}
            aria-label={none}
            aria-pressed={hex === null}
            onClick={() => onPick(null, true)}
            className={`relative h-7 w-7 overflow-hidden rounded-full bg-white ring-1 ring-black/10 ${
              hex === null ? 'ring-2 ring-blue-600 ring-offset-1' : ''
            }`}
          >
            <span className="absolute left-1/2 top-1/2 h-[1.5px] w-8 -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-red-500" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function Swatch({
  color,
  name,
  on,
  onClick,
}: {
  color: string;
  name: string;
  on: boolean;
  onClick: () => void;
}): ReactNode {
  const dark = (() => {
    const value = Number.parseInt(color.slice(1), 16);
    const r = (value >> 16) & 255;
    const g = (value >> 8) & 255;
    const b = value & 255;
    return 0.299 * r + 0.587 * g + 0.114 * b < 150;
  })();
  return (
    <button
      type="button"
      title={name}
      aria-label={name}
      aria-pressed={on}
      onClick={onClick}
      className={`grid h-7 w-7 place-items-center rounded-full ring-1 ring-black/10 ${on ? 'ring-2 ring-blue-600 ring-offset-1' : ''}`}
      style={{ background: color }}
    >
      {on ? <Check className={`h-3.5 w-3.5 ${dark ? 'text-white' : 'text-slate-900'}`} /> : null}
    </button>
  );
}

/** 고르는 상자(글꼴·화면 크기). */
export function SelectBox({
  value,
  options,
  onChange,
  label,
}: {
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (value: string) => void;
  label: string;
}): ReactNode {
  return (
    <select
      aria-label={label}
      className="h-9 w-full cursor-pointer rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-slate-800 outline-none focus:border-blue-500"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/**
 * 입력 칸의 값 — 그 칸과 같은 종류의 칸으로 받는다(날짜면 달력, 숫자면 숫자). 글·숫자는 치고 Enter 나 다른 곳을 누르면,
 * 날짜·시각·색은 고르는 즉시 반영된다.
 */
export function ValueField({
  label,
  type,
  value,
  onChange,
}: {
  label: string;
  /** `<input>` 의 type — text·number·date·time·month·color 등. */
  type: string;
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  // 날짜는 년·월·일이 다 보이는 날짜 칸 + 달력(`DateField`) — 브라우저 날짜 칸은 로캘마다 모양이 다르고 좁으면 잘린다.
  if (type === 'date') return <DateField value={value} onChange={onChange} label={label} wide />;
  const instant = ['date', 'datetime-local', 'month', 'week', 'time', 'color'].includes(type);
  return (
    <input
      aria-label={label}
      type={type}
      className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-[14px] text-slate-900 outline-none focus:border-blue-500"
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        if (instant) onChange(event.target.value);
      }}
      onBlur={() => {
        if (!instant && text !== value) onChange(text);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
      }}
    />
  );
}

/** 여러 줄 글 — 다른 곳을 누르면 반영된다(선택 상자의 항목·여러 줄 입력 칸의 값). */
export function LinesField({
  label,
  value,
  onChange,
  rows = 4,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}): ReactNode {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <textarea
      aria-label={label}
      rows={rows}
      className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13.5px] leading-relaxed text-slate-900 outline-none focus:border-blue-500"
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        if (text !== value) onChange(text);
      }}
    />
  );
}
